import { useState, useEffect, useCallback, useRef } from "react";
import { Container, Row, Col, Spinner } from "react-bootstrap";
import markerIcon from "./images/marker.svg";
import clusterIcon from "./images/cluster.svg";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPhoneVolume } from "@fortawesome/free-solid-svg-icons";
import FacilitiesData from "./data/facilities.json";
import { GoogleMap, useLoadScript, InfoWindow } from "@react-google-maps/api";
import { MarkerClusterer } from "@googlemaps/markerclusterer";
import mapStyles from ".//styles/mapStyles";
import FilterDropdown from "./components/FilterDropdown";
import RegionButtons from "./components/RegionButtons";
import FacilityCard from "./components/FacilityCard";
import AddressLink from "./components/AddressLink";
import VirtualTourLinks from "./components/VirtualTourLinks";
import { createFilterActions } from "./utils/filterConfig";
import "./styles/index.scss";

const filteredFacilitiesData = FacilitiesData.filter((item) => item.active);

const mapContainerStyle = {
  width: "100%",
  height: "500px",
};

const options = {
  styles: mapStyles,
  disableDefaultUI: true,
  zoomControl: true,
};

const displayAvailableBeds = (category) => {
  if (category === "Behavioral Health") {
    return (
      <a href={`/bedroom-availability/`} className="btn-gen-dark btn" target="_parent">
        View Available Beds
      </a>
    );
  }
  return null;
};

const Locations = () => {
  const [selectedValue, setSelectedValue] = useState("all");
  const [category, setCategory] = useState("ALL");
  const [categoryTitle, setCategoryTitle] = useState("All");

  const [zoom, setZoom] = useState(6);
  const [center, setCenter] = useState({
    lat: 36.32922453743021,
    lng: -119.23217851501158,
  });

  function setZoomCenterDefault() {
    setZoom(6);
    setCenter({
      lat: 36.32922453743021,
      lng: -119.23217851501158,
    });
  }

  function setALL() {
    setCategory("ALL");
    setCategoryTitle("All");
    setZoomCenterDefault();
  }
  function setAL() {
    setCategory("AL");
    setCategoryTitle("Assisted Living");
    setZoomCenterDefault();
  }
  function setBH() {
    setCategory("BH");
    setCategoryTitle("Behavioral Health");
    setZoomCenterDefault();
  }
  function setPH() {
    setCategory("PH");
    setCategoryTitle("Psychiatric Health");
    setZoom(7);
    setCenter({
      lat: 33.483738966272,
      lng: -117.10065754232862,
    });
  }
  function setMC() {
    setCategory("MC");
    setCategoryTitle("Memory Care");
    setCenter({
      lat: 38.196676924161814,
      lng: -122.45413461384996,
    });
  }
  function setMHRC() {
    setCategory("MHRC");
    setCategoryTitle("Mental Health Rehabilitation Centers");
    setZoom(7);
    setCenter({
      lat: 34.196676924161814,
      lng: -117.45413461384996,
    });
  }
  function setSN() {
    setCategory("SN");
    setCategoryTitle("Skilled Nursing");
    setZoomCenterDefault();
  }
  function setOT() {
    setCategory("OT");
    setCategoryTitle("Outpatient Therapy");
    setZoomCenterDefault();
  }
  function setAR() {
    setCategory("AR");
    setCategoryTitle("Adult Residential");
    setZoom(6);
    setCenter({
      lat: 35.943407746368024,
      lng: -121.10599532883568,
    });
  }
  function setSR() {
    setCategory("SR");
    setCategoryTitle("Substance Recovery");
    setZoom(7);
    setCenter({
      lat: 33.483738966272,
      lng: -117.10065754232862,
    });
  }

  function setCAN() {
    setCategory("CA-N");
    setCategoryTitle("North California");
    setZoom(7);
    setCenter({
      lat: 38.196676924161814,
      lng: -122.45413461384996,
    });
    setSelectedValue("select");
  }
  function setCAC() {
    setCategory("CA-C");
    setCategoryTitle("Central California");
    setZoom(7);
    setCenter({
      lat: 36.438312335577976,
      lng: -119.80349478026108,
    });
    setSelectedValue("select");
  }
  function setCAS() {
    setCategory("CA-S");
    setCategoryTitle("South California");
    setZoom(7);
    setCenter({
      lat: 33.43101155668484,
      lng: -117.61469312662732,
    });
    setSelectedValue("select");
  }

  const filterConfig = createFilterActions({
    setALL,
    setSN,
    setBH,
    setPH,
    setAL,
    setMC,
    setMHRC,
    setOT,
    setAR,
    setSR,
  });

  //applyFilter function, whenever any sets are called this runs to apply the filter
  const applyFilter = useCallback(
    (filterCode, updateUrl = false) => {
      const config = filterConfig[filterCode];
      if (config) {
        setSelectedValue(filterCode);
        config.func();
        if (updateUrl) {
          const newUrl = new URL(window.location);
          newUrl.searchParams.set("filter", filterCode);
          window.history.pushState({}, "", newUrl);
        }
      } else {
        console.warn(`Unknown filter: ${filterCode}`);
      }
    },
    [setALL, setSN, setBH, setPH, setAL, setMC, setMHRC, setOT, setAR, setSR, setSelectedValue]
  );

  //URL useEffect, searches for filter param
  useEffect(() => {
    const searchParams = new URLSearchParams(window.location.search);
    const filter = searchParams.get("filter");

    if (filter) {
      const config = filterConfig[filter];
      if (config) {
        setSelectedValue(filter);
        config.func();
      }
    }
  }, []);

  // Popstate event listener to handle back/forward navigation
  useEffect(() => {
    const handlePopState = () => {
      const searchParams = new URLSearchParams(window.location.search);
      const filter = searchParams.get("filter");

      if (filter) {
        const config = filterConfig[filter];
        if (config) {
          setSelectedValue(filter);
          config.func();
        }
      } else {
        setSelectedValue("all");
        setALL();
      }
    };

    window.addEventListener("popstate", handlePopState);

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [filterConfig, setALL]);

  const handleSelectChange = (event) => {
    const newValue = event.target.value;
    applyFilter(newValue, true);
  };

  let results = filteredFacilitiesData.filter((facility) => {
    return category === "ALL" ? filteredFacilitiesData : facility.category.includes(category);
  });

  const { isLoaded, loadError } = useLoadScript({
    googleMapsApiKey: process.env.REACT_APP_GOOGLE_MAPS_API_KEY,
    //the apikey was just pasted raw here, I removed it and it's using the env file now.
  });
  const [selected, setSelected] = useState(null);
  const [map, setMap] = useState(null);
  const markersRef = useRef([]);
  const markerClustererRef = useRef(null);
  const isInitialLoad = useRef(true);

  // useEffect to manage markers and clusters
  useEffect(() => {
    if (!map) return;

    markersRef.current.forEach((marker) => marker.setMap(null));
    if (markerClustererRef.current) {
      markerClustererRef.current.clearMarkers();
    }

    // Create individual markers with slight position offsets for facilities at same location
    const newMarkers = results.map((facility, index) => {
      const baseLat = facility.coordinates[0];
      const baseLng = facility.coordinates[1];

      const facilitiesAtSameLocation = results.filter(
        (f) => f.coordinates[0] === baseLat && f.coordinates[1] === baseLng
      );

      let adjustedLat = baseLat;
      let adjustedLng = baseLng;

      if (facilitiesAtSameLocation.length > 1) {
        const facilityIndex = facilitiesAtSameLocation.findIndex((f) => f.id === facility.id);
        const offsetDistance = 0.0001;
        const angle = (facilityIndex * 360) / facilitiesAtSameLocation.length;

        adjustedLat = baseLat + offsetDistance * Math.sin((angle * Math.PI) / 180);
        adjustedLng = baseLng + offsetDistance * Math.cos((angle * Math.PI) / 180);
      }

      const marker = new window.google.maps.Marker({
        position: { lat: adjustedLat, lng: adjustedLng },
        map: map,
        icon: {
          url: markerIcon,
          scaledSize: new window.google.maps.Size(40, 40),
        },
        // Only animate on initial load
        animation: isInitialLoad.current ? window.google.maps.Animation.DROP : null,
      });

      marker.addListener("click", (event) => {
        // Prevent default InfoWindow
        event.stop && event.stop();
        setSelected(facility);
      });

      return marker;
    });

    markersRef.current = newMarkers;
    isInitialLoad.current = false;

    const clusterer = new MarkerClusterer({
      map,
      markers: newMarkers,
      gridSize: 60,
      minimumClusterSize: 2,
      renderer: {
        render: ({ count, position }) => {
          return new window.google.maps.Marker({
            position,
            icon: {
              url: clusterIcon,
              scaledSize: new window.google.maps.Size(50, 50),
              labelOrigin: new window.google.maps.Point(25, 15),
            },
            label: {
              text: count.toString(),
              color: "#000000ff",
              fontSize: "14px",
              fontWeight: "bold",
            },
            zIndex: 1000,
          });
        },
      },
    });

    markerClustererRef.current = clusterer;
  }, [map, results]);

  if (loadError) return "Error loading maps";
  if (!isLoaded) return <Spinner animation="border" variant="primary" />;

  return (
    <>
      <section fluid="true" className="location-section pt-5 pb-5">
        <Container className="location-filter p-4">
          <Row>
            <FilterDropdown
              selectedValue={selectedValue}
              handleSelectChange={(event) => {
                handleSelectChange(event);
              }}
            />
            <Col sm={12} md={7} className="text-end ">
              <RegionButtons setCAN={setCAN} setCAC={setCAC} setCAS={setCAS} />
            </Col>
          </Row>
        </Container>

        <Container className="location-map p-0" fluid="true">
          <Row className="m-0">
            <Col className="p-0">
              <div className="location-map__height">
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  zoom={zoom}
                  center={center}
                  options={options}
                  opacity="0.1"
                  onLoad={(mapInstance) => {
                    setMap(mapInstance);
                  }}
                >
                  {selected ? (
                    <InfoWindow
                      position={{
                        lat: selected.coordinates[0],
                        lng: selected.coordinates[1],
                      }}
                      onCloseClick={() => {
                        setSelected(null);
                      }}
                    >
                      <div className="location-map__info-window">
                        <div>
                          <h3 style={{ fontSize: "1.2em", marginBottom: "10px" }}>{selected.name}</h3>
                          <p>
                            <AddressLink facility={selected} />
                          </p>
                          <p>
                            <FontAwesomeIcon icon={faPhoneVolume} size="1x" className="text-green" />
                            {"  "}
                            <a className="phone-link" href={`tel:${selected.phone}`}>
                              <b>{selected.phone}</b>
                            </a>
                          </p>
                          <p>
                            {/* {selected.id === 26 ? (
                              <span>Coming Soon!</span>
                            ) : (
                              <a
                                href={`${selected.url}`}
                                target={`${selected.target}`}
                                className="btn-gen-green btn view-facility-btn"
                              >
                                View Facility
                              </a>
                            )} */}

                            <a
                              href={`${selected.url}`}
                              target={`${selected.target}`}
                              className="btn-gen-green btn view-facility-btn"
                            >
                              View Facility
                            </a>
                          </p>
                          <span className="location-popup-links">
                            <VirtualTourLinks facility={selected} isPopup={true} />
                          </span>
                        </div>
                      </div>
                    </InfoWindow>
                  ) : null}
                </GoogleMap>
              </div>
            </Col>
          </Row>
        </Container>

        <Container className="location-list">
          <Row>
            <Col className="text-center">
              <h2 className="location-list__title mt-5 mb-4">{categoryTitle} Facilities</h2>
              {displayAvailableBeds(categoryTitle)}
            </Col>
          </Row>
          <Row>
            {results.map((selected, key) => (
              <Col xs={12} sm={6} md={4} lg={3} className="mb-4" key={key}>
                <FacilityCard facility={selected} />
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </>
  );
};

export default Locations;
